/**
 * Тонкий API-клиент для FastAPI backend.
 * Access-token хранится в памяти (sessionStorage — для refresh после F5).
 * При 401 защищённого запроса AuthContext очищает сессию и ведёт на /login.
 */

const ACCESS_KEY = 'mp_trend.access';
const REFRESH_KEY = 'mp_trend.refresh';

export type Tokens = { access_token: string; refresh_token: string };
type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(
  handler: UnauthorizedHandler | null,
): void {
  unauthorizedHandler = handler;
}

function handleUnauthorized(): void {
  unauthorizedHandler?.();
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}

export function setTokens(tokens: Tokens): void {
  sessionStorage.setItem(ACCESS_KEY, tokens.access_token);
  sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
}

export function clearTokens(): void {
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

export class ApiError extends Error {
  status: number;
  detail?: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

type ApiErrorPayload = {
  detail?: unknown;
};

const VALIDATION_FIELD_LABELS: Record<string, string> = {
  username: 'Логин',
  password: 'Пароль',
  role: 'Роль',
  email: 'Email',
  telegram_chat_id: 'Telegram',
  is_active: 'Статус',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function validationFieldLabel(loc: unknown): string {
  if (!Array.isArray(loc)) return 'Поле';
  const field = loc.filter((item) => typeof item === 'string').at(-1);
  if (!field) return 'Поле';
  return VALIDATION_FIELD_LABELS[field] ?? field;
}

function formatValidationMessage(error: Record<string, unknown>): string {
  const type = typeof error.type === 'string' ? error.type : '';
  const msg =
    typeof error.msg === 'string' ? error.msg : 'некорректное значение';
  const ctx = isRecord(error.ctx) ? error.ctx : {};

  if (type === 'string_too_short' && typeof ctx.min_length === 'number') {
    return `минимум ${ctx.min_length} символов`;
  }
  if (type === 'string_too_long' && typeof ctx.max_length === 'number') {
    return `максимум ${ctx.max_length} символов`;
  }
  if (type === 'missing') {
    return 'обязательное поле';
  }
  return msg;
}

function formatValidationError(error: unknown): string {
  if (!isRecord(error)) return String(error);
  const field = validationFieldLabel(error.loc);
  return `${field}: ${formatValidationMessage(error)}`;
}

export function formatApiErrorMessage(
  detail: unknown,
  fallback: string,
): string {
  if (!isRecord(detail) || !('detail' in detail)) return fallback;

  const apiDetail = (detail as ApiErrorPayload).detail;
  if (typeof apiDetail === 'string') return apiDetail;
  if (Array.isArray(apiDetail))
    return apiDetail.map(formatValidationError).join('; ');
  if (isRecord(apiDetail) && typeof apiDetail.message === 'string')
    return apiDetail.message;
  return fallback;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  withAuth = true,
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');

  if (withAuth) {
    const access = getAccessToken();
    if (access) headers.set('Authorization', `Bearer ${access}`);
  }

  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    let detail: unknown = undefined;
    try {
      detail = await response.json();
    } catch {
      /* non-json body */
    }
    if (response.status === 401 && withAuth) {
      handleUnauthorized();
    }
    const message = formatApiErrorMessage(detail, response.statusText);
    throw new ApiError(response.status, message, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

async function requestText(
  path: string,
  init: RequestInit = {},
  withAuth = true,
): Promise<string> {
  const headers = new Headers(init.headers ?? {});

  if (withAuth) {
    const access = getAccessToken();
    if (access) headers.set('Authorization', `Bearer ${access}`);
  }

  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    let detail: unknown = undefined;
    try {
      detail = await response.json();
    } catch {
      /* non-json body */
    }
    if (response.status === 401 && withAuth) {
      handleUnauthorized();
    }
    const message = formatApiErrorMessage(detail, response.statusText);
    throw new ApiError(response.status, message, detail);
  }
  return response.text();
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
  getText: (path: string, headers?: HeadersInit, withAuth = false) =>
    requestText(path, { method: 'GET', headers }, withAuth),
  postPublic: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }, false),
};

export function filenameFromContentDisposition(
  contentDisposition: string | null,
  fallback = 'download',
): string {
  const header = contentDisposition ?? '';
  const starMatch = header.match(/filename\*=([^;]+)/i);
  if (starMatch?.[1]) {
    const raw = starMatch[1].trim().replace(/^"|"$/g, '');
    const utf8 = raw.match(/^UTF-8''(.+)$/i);
    const encoded = utf8?.[1] ?? raw;
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded || fallback;
    }
  }
  const match = header.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

/**
 * Скачивание бинарного файла (PDF/CSV/XLSX) с Bearer-JWT.
 * Делаем через fetch + blob — браузер сам вызовет Save As через clickable <a>.
 */
export async function downloadFile(path: string): Promise<void> {
  const headers = new Headers();
  const access = getAccessToken();
  if (access) headers.set('Authorization', `Bearer ${access}`);
  const response = await fetch(path, { headers });
  if (!response.ok) {
    let detail: unknown = undefined;
    try {
      detail = await response.json();
    } catch {
      /* non-json body */
    }
    if (response.status === 401) {
      handleUnauthorized();
    }
    const message = formatApiErrorMessage(detail, response.statusText);
    throw new ApiError(response.status, message, detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const filename = filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadPostFile(path: string, body: unknown): Promise<void> {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  const access = getAccessToken();
  if (access) headers.set('Authorization', `Bearer ${access}`);
  const response = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let detail: unknown = undefined;
    try {
      detail = await response.json();
    } catch {
      /* non-json body */
    }
    if (response.status === 401) {
      handleUnauthorized();
    }
    const message = formatApiErrorMessage(detail, response.statusText);
    throw new ApiError(response.status, message, detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const filename = filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ===== Типы доменной модели =====

export type UserRole = 'admin' | 'user' | 'viewer';
export type UiTheme = 'dark' | 'light';

export type User = {
  id: string;
  username: string;
  email: string | null;
  telegram_chat_id: string | null;
  role: UserRole;
  is_admin: boolean;
  is_active: boolean;
  notify_email_enabled: boolean;
  notify_telegram_enabled: boolean;
  notify_on_success: boolean;
  notify_on_failure: boolean;
  persist_table_sorting: boolean;
  persist_table_filters: boolean;
  show_ui_hints: boolean;
  ui_theme: UiTheme;
  created_at?: string | null;
  updated_at?: string | null;
};

export type UserProfileUpdate = {
  email: string | null;
  telegram_chat_id: string | null;
  notify_email_enabled: boolean;
  notify_telegram_enabled: boolean;
  notify_on_success: boolean;
  notify_on_failure: boolean;
  persist_table_sorting: boolean;
  persist_table_filters: boolean;
  show_ui_hints: boolean;
  ui_theme: UiTheme;
};

export type AdminUserCreate = UserProfileUpdate & {
  username: string;
  password: string;
  role: UserRole;
  is_active: boolean;
};

export type AdminUserUpdate = UserProfileUpdate & {
  role: UserRole;
  is_active: boolean;
};

export type CategoryNode = {
  name: string;
  path: string;
};

export type Marketplace = 'WB' | 'OZON' | 'YM';

export type RunStatus = 'CREATED' | 'RUNNING' | 'SUCCESS' | 'FAILED';
export type ReportExportBatchStatus = 'CREATED' | 'READY' | 'FAILED';

export type Run = {
  id: string;
  title: string;
  marketplace: Marketplace;
  month: string;
  status: RunStatus;
  started_at: string | null;
  finished_at: string | null;
  params: Record<string, unknown> | null;
  stats: Record<string, unknown> | null;
  error: string | null;
  triggered_by_user_id: string | null;
  actions: RunActionPermissions;
  created_at: string;
  attempts?: RunAttempt[];
};

export type RunActionPermissions = {
  can_retry: boolean;
  can_delete: boolean;
  can_edit: boolean;
  can_cancel: boolean;
};

export type RunAttempt = {
  id: string;
  report_run_id: string;
  attempt_no: number;
  status: RunStatus;
  started_at: string | null;
  finished_at: string | null;
  stats: Record<string, unknown> | null;
  error: string | null;
};

export type RunsPage = {
  items: Run[];
  total: number;
  limit: number;
  offset: number;
  sort_by: string;
  sort_dir: 'asc' | 'desc';
};

export type Thresholds = {
  new_winner_min_revenue: number;
  growth_rate: number;
  min_prev_revenue: number;
};

export type RunCreateBody = {
  title?: string | null;
  month: string; // YYYY-MM-DD
  marketplace: Marketplace;
  categories_scope: string[];
  thresholds?: Thresholds;
  llm_enabled: boolean;
  bhapi_top_n?: number;
};

export type RunPreflight = {
  month: string;
  marketplace: Marketplace;
  categories_count: number;
  estimated_mpstats_requests: number;
  api_limit_snapshot: Record<string, unknown> | null;
  warnings: string[];
  can_start: boolean;
};

export type RunScheduleStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'DELETED';
export type RunScheduleFrequency = 'MONTHLY' | 'WEEKLY' | 'BIWEEKLY';
export type RunScheduleMonthPolicy = 'PREVIOUS_COMPLETED' | 'CURRENT' | 'FIXED';

export type RunSchedulesPage = {
  items: RunSchedule[];
  total: number;
  limit: number;
  offset: number;
  sort_by: string;
  sort_dir: 'asc' | 'desc';
};

export type RunSchedule = {
  id: string;
  title: string;
  marketplace: Marketplace;
  status: RunScheduleStatus;
  frequency: RunScheduleFrequency;
  month_policy: RunScheduleMonthPolicy;
  fixed_month: string | null;
  day_of_month: number | null;
  day_of_week: number | null;
  start_date: string | null;
  hour: number;
  minute: number;
  timezone: string;
  categories_scope: string[];
  thresholds: Record<string, unknown> | null;
  llm_enabled: boolean;
  bhapi_top_n: number | null;
  created_by_user_id: string | null;
  last_run_at: string | null;
  next_run_at: string;
  cancelled_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
};

export type RunScheduleBody = {
  title?: string | null;
  marketplace: Marketplace;
  frequency: RunScheduleFrequency;
  month_policy: RunScheduleMonthPolicy;
  fixed_month?: string | null;
  day_of_month?: number | null;
  day_of_week?: number | null;
  start_date?: string | null;
  hour: number;
  minute: number;
  timezone: string;
  categories_scope: string[];
  thresholds?: Thresholds;
  llm_enabled: boolean;
  bhapi_top_n?: number;
};

export type RunLogEntry = {
  id: string;
  report_run_id: string;
  created_at: string;
  level: string;
  stage: string | null;
  message: string;
  payload: Record<string, unknown> | null;
};

export type RunsSettings = {
  max_concurrent: number;
  concurrency_scope: 'global' | 'per_marketplace';
  stale_minutes: number;
};

export type RunPermissionScope = 'none' | 'own' | 'all';

export type RunRolePermissions = {
  retry: RunPermissionScope;
  delete: RunPermissionScope;
  edit: RunPermissionScope;
  cancel: RunPermissionScope;
};

export type RunPermissions = {
  user: RunRolePermissions;
  viewer: RunRolePermissions;
};

export type ProductDetails = {
  source: 'card_details' | 'basic' | string;
  external_url: string | null;
  main_image: string | null;
  images: string[];
  description: string | null;
  complectation: string | null;
  category_name: string | null;
  category_path: string | null;
  product_props: Record<string, unknown> | null;
  shop_info: Record<string, unknown> | null;
  availability: string | null;
  price_info: Record<string, unknown> | null;
  raw_attributes: Record<string, unknown> | null;
};

export type TrendEvent = {
  id: string;
  event_type: 'NEW_WINNER' | 'OLD_CARD_HYPER_GROWTH' | string;
  month: string;
  sku_external_id: string;
  sku_title: string | null;
  brand: string | null;
  seller: string | null;
  seller_external_id: string | null;
  category_path: string | null;
  first_seen_date: string | null;
  revenue_current_rub: number | null;
  revenue_previous_rub: number | null;
  revenue_delta_rub: number | null;
  growth_rate: number | null;
  sales_current: number | null;
  sales_previous: number | null;
  sales_delta: number | null;
  final_price_current: number | null;
  final_price_previous: number | null;
  rating_current: number | null;
  comments_current: number | null;
  comments_previous: number | null;
  balance_current: number | null;
  balance_previous: number | null;
  comments_delta: number | null;
  price_delta: number | null;
  balance_delta: number | null;
  signals: Record<string, unknown> | null;
  llm_text: string | null;
  llm_drivers: string[] | null;
  external_url: string | null;
  product_details: ProductDetails | null;
};

export type Section3Row = {
  entity_type?: 'brand' | 'seller' | 'warning' | string;
  name?: string | null;
  brand?: string | null;
  seller?: string | null;
  seller_id?: string | number | null;
  category_path?: string | null;
  source?: string | null;
  source_note?: string | null;
  revenue?: number | null;
  sales?: number | null;
  message?: string | null;
};

export type Report = {
  run: Run;
  section_1_new_winners: TrendEvent[];
  section_2_hyper_growth: TrendEvent[];
  section_3_new_brands_sellers: Section3Row[];
  section_4_winner_patterns: string | null;
  section_5_hyper_patterns: string | null;
  section_6_hypotheses: string | null;
  section_7_recommendations: string | null;
  stages?: Array<{
    name: string;
    status: string;
    started_at: string;
    finished_at?: string | null;
    duration_sec?: number | null;
    error?: string | null;
  }> | null;
};

export type ReportExportBatch = {
  id: string;
  created_at: string;
  updated_at: string;
  marketplace: Marketplace;
  month: string;
  requested_by_user_id: string | null;
  status: ReportExportBatchStatus;
  formats: string[];
  report_run_ids: string[];
  file_name: string | null;
  file_size_bytes: number | null;
  content_type: string | null;
  error_message: string | null;
};

export type ReportExportBatchCreate = {
  marketplace: Marketplace;
  month: string;
  formats: string[];
};
